import { useEffect, useState, useRef } from 'react';
import styled from 'styled-components';
import Header from '../components/Header';
import '../index.css';
import CustomTable, { Column } from '../components/CustomTable';
import CustomerModal from '../components/CustomerModal';
import moment from 'moment';
import apiClient from '../services/apiClient';
import EmptyState from '../components/EmptyState';
import CustomerIcon from '../assets/svgs/CustomerIcon';
import ReactGA from 'react-ga4';
import { CreateButton } from '../components/SharedComponents';
import Tabbed from '../components/Tabbed';
import CustomerGroups, { CustomerGroupsRef } from '../components/CustomerGroups';

const Wrapper = styled.div`
	background-color: white;
	width: 100%;
	min-height: 100vh;
	padding: 20px 20px;
	box-sizing: border-box;
	@media (max-width: 768px) {
		padding: 20px 20px;
	}
`;

interface Customer {
	id: number;
	name: string;
	email: string | null;
	phone: string | null;
	address: string | null;
	notes: string | null;
	date_created: string;
	date_updated: string;
}

export default function Customers() {
	const [isEditPanelVisible, setIsEditPanelVisible] = useState<boolean>(false);
	const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
	const [customers, setCustomers] = useState<Customer[]>([]);
	const [fetchState, setFetchState] = useState<'loading' | 'success' | 'failed'>('loading');
	const [isNewCustomer, setIsNewCustomer] = useState(false);
	const [activeHeaderButtons, setActiveHeaderButtons] = useState<React.ReactElement[]>([]);
	const customerGroupsRef = useRef<CustomerGroupsRef>(null);

	// Columns definition for CustomTable
	const columns: Column[] = [
		{
			field: 'name',
			headerName: 'Name',
			minWidth: 200,
			headerClassName: 'table-header',
			cellClassName: 'font-medium text-gray-900',
			flex: 2,
		},
		{
			field: 'phone',
			headerName: 'Phone',
			minWidth: 150,
			headerClassName: 'table-header',
			cellClassName: 'text-gray-500',
			renderCell: (params) => params.value || '-',
			flex: 1,
		},
		{
			field: 'address',
			headerName: 'Address',
			minWidth: 250,
			headerClassName: 'table-header',
			cellClassName: 'text-gray-500',
			renderCell: (params) => params.value || '-',
			flex: 2,
		},
		{
			field: 'email',
			headerName: 'Email',
			minWidth: 200,
			headerClassName: 'table-header',
			cellClassName: 'text-gray-500',
			renderCell: (params) => params.value || '-',
			flex: 2,
		},
		{
			field: 'date_created',
			headerName: 'Customer Since',
			minWidth: 150,
			headerClassName: 'table-header',
			cellClassName: 'text-gray-500',
			renderCell: (params) => moment(params.value).format('Do MMM YYYY'),
			flex: 1,
		},
	];

	useEffect(() => {
		ReactGA.send({
			hitType: 'pageview',
			page: '/customers',
			title: 'Customers',
		});

		fetchCustomers();

		// Initialize active header buttons for the first tab
		setActiveHeaderButtons([
			<CreateButton key="create-button" onClick={showCreateCustomerPanel}>
				<CustomerIcon />
				<span>Add Customer</span>
			</CreateButton>,
		]);
	}, []);

	const fetchCustomers = () => {
		setFetchState('loading');
		apiClient
			.get('/customers')
			.then((response: any) => {
				setFetchState('success');
				const customersData = Array.isArray(response.data)
					? response.data
					: response.data?.data || response || [];
				setCustomers(customersData);
				ReactGA.event({
					category: 'Customers',
					action: 'Fetched Customers',
					label: 'Fetch Success',
				});
			})
			.catch((error) => {
				console.error('Error fetching customers:', error);
				setFetchState('failed');
				setCustomers([]); // Set empty array on error
				ReactGA.event({
					category: 'Customers',
					action: 'Fetch Customers Failed',
					label: 'Fetch Failed',
				});
			});
	};

	const handleRowClick = (params: { row: Customer }) => {
		const customer = params.row;
		setSelectedCustomer(customer);
		setIsNewCustomer(false);
		setIsEditPanelVisible(true);

		ReactGA.event({
			category: 'User Interaction',
			action: 'Clicked Customer Row',
			label: `Customer ID: ${customer.id}`,
		});
	};

	const showCreateCustomerPanel = () => {
		ReactGA.event({
			category: 'User Interaction',
			action: 'Clicked Add New Customer Button',
			label: 'Add New Customer',
		});
		setSelectedCustomer(null);
		setIsNewCustomer(true);
		setIsEditPanelVisible(true);
	};

	const showCreateCustomerGroupPanel = () => {
		ReactGA.event({
			category: 'User Interaction',
			action: 'Clicked Add New Customer Group Button',
			label: 'Add New Customer Group',
		});
		if (customerGroupsRef.current) {
			customerGroupsRef.current.showCreatePanel();
		}
	};

	const handleCustomerUpdate = (updatedCustomer: any) => {
		// Update the customer in the list
		if (updatedCustomer.id) {
			setCustomers(customers.map((c) => (c.id === updatedCustomer.id ? updatedCustomer : c)));
		}
	};

	const handleCustomerDelete = (customerId: number) => {
		// Remove the customer from the list
		setCustomers(customers.filter((c) => c.id !== customerId));
		setIsEditPanelVisible(false);
		ReactGA.event({
			category: 'Customers',
			action: 'Deleted Customer',
			label: `Customer ID: ${customerId}`,
		});
	};

	const hideCreateCustomerPanel = (customer: Customer | null = null) => {
		const wasCreatingNew = isNewCustomer;
		setIsEditPanelVisible(false);
		setSelectedCustomer(null);
		setIsNewCustomer(false);

		if (customer) {
			if (wasCreatingNew) {
				// This is a new customer - add it to the list
				setCustomers([...customers, customer]);
				ReactGA.event({
					category: 'Customers',
					action: 'Created New Customer',
					label: 'New Customer',
				});
			} else {
				// This is an update to existing customer
				setCustomers(
					customers.map((c: Customer) => {
						if (c.id === customer.id) {
							return customer;
						}
						return c;
					})
				);
				ReactGA.event({
					category: 'Customers',
					action: 'Updated Customer',
					label: `Customer ID: ${customer.id}`,
				});
			}
		} else {
			ReactGA.event({
				category: 'User Interaction',
				action: 'Closed Customer Panel',
				label: 'No Customer',
			});
		}
	};

	const customersTable = (
		<>
			<CustomTable
				columns={columns}
				objects={customers || []}
				sx={{ marginTop: '50px' }}
				onRowClick={handleRowClick}
				fetchState={fetchState}
				initialRowsPerPage={25}
				emptyStateComponent={() => (
					<EmptyState
						icon={<CustomerIcon />}
						title="No customers yet"
						description="Add your first customer to get started"
					/>
				)}
			/>
			<CustomerModal
				isVisible={isEditPanelVisible}
				handleHidePanel={hideCreateCustomerPanel}
				customer={selectedCustomer}
				isNewCustomer={isNewCustomer}
				onCustomerUpdate={handleCustomerUpdate}
				onCustomerDelete={handleCustomerDelete}
			/>
		</>
	);

	const sections = [
		{
			label: 'Customers',
			component: customersTable,
		},
		{
			label: 'Groups',
			component: <CustomerGroups ref={customerGroupsRef} />,
		},
	];

	const handleTabChange = (index: number) => {
		if (index === 0) {
			// Customers tab
			setActiveHeaderButtons([
				<CreateButton key="create-button" onClick={showCreateCustomerPanel}>
					<CustomerIcon />
					<span>Add Customer</span>
				</CreateButton>,
			]);
		} else if (index === 1) {
			// Groups tab
			setActiveHeaderButtons([
				<CreateButton key="create-button" onClick={showCreateCustomerGroupPanel}>
					<CustomerIcon />
					<span>Add Group</span>
				</CreateButton>,
			]);
		}
	};

	return (
		<Wrapper>
			<Header
				title="Customers"
				description="Manage your customers and view their purchase history"
				headerComponents={activeHeaderButtons}
			/>
			<Tabbed sections={sections} onChange={handleTabChange} />
		</Wrapper>
	);
}
